import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import "@/pages/Press.css";

/* ------------------------------------------------------------------
   PRESS — premium editorial archive.
   Data co-located here (parallels homepage RecognitionPress) so the
   archive can evolve independently. Article screenshots live in
   /public/press/.
   ------------------------------------------------------------------ */
const ARTICLES = [
  {
    key: "ad",
    image: "/press/ad_card.png",
    name: "Architectural Digest",
    title:
      "This multigenerational Bengaluru home basks in warm memories",
    href: "https://www.architecturaldigest.in/story/this-multi-generational-bengaluru-home-basks-in-the-afterglow-of-warm-memories-the-hippodrome-setting/",
  },
  {
    key: "elle",
    image: "/press/elle_card.png",
    name: "Elle Decor",
    title: "This Bengaluru home is an ode to clean lines",
    href: "https://elledecor.in/bengaluru-home-designed-the-hippodrome-setting-ode-clean-lines/",
  },
  {
    key: "gg",
    image: "/press/gg_card.png",
    name: "G&G Magazine",
    title: "International magazine cover feature",
    href: "https://issuu.com/casaliving_it/docs/tender_peach_fuzz_january_2024",
  },
  {
    key: "gh",
    image: "/press/goodhomes_card.png",
    name: "GoodHomes",
    title:
      "A home where every space resonates with each other",
    href: "https://www.goodhomes.co.in/home-decor/tie-untie-a-home-where-every-space-resonates-with-each-other-8988.html",
  },
  {
    key: "tad",
    image: "/press/tad_card.png",
    name: "The Architect's Diary",
    title: "A glass-front facade shaped by natural light",
    href: "https://thearchitectsdiary.com/the-glass-front-facade-articulated-the-benefits-of-natural-light-the-hippodrome-setting/",
  },
];

/* Card transform model.
   `local` = pos - i where pos = progress * (N + 0.4).
   • local <= -2  → off-stage (out of sight, parked low)
   • -2..-1       → queued: peeks from BELOW the active card
   • -1..0        → entering: slides up to active position
   •   0..3       → receding: settles up & back, gentle rotation
   •  >3          → caps to the back of the stack
   z-index is highest closest to active so the active card sits on top.   */
function cardStyle(local) {
  let y;
  let scale;
  let rotate;
  let opacity;

  if (local <= -2) {
    y = 60;
    scale = 0.9;
    rotate = 0;
    opacity = 0;
  } else if (local <= -1) {
    // Queued: peeks below the active card.
    const t = local + 2; // 0 → 1
    y = 44 + (1 - t) * 12; // 56 → 44
    scale = 0.92 + t * 0.02; // 0.92 → 0.94
    rotate = 0;
    opacity = 0.18 + t * 0.18; // 0.18 → 0.36
  } else if (local <= 0) {
    // Entering to active.
    const t = local + 1; // 0 → 1
    y = 44 * (1 - t); // 44 → 0
    scale = 0.94 + t * 0.06; // 0.94 → 1
    rotate = 0;
    opacity = 0.36 + t * 0.64; // 0.36 → 1
  } else {
    // Receding behind newer cards.
    const r = Math.min(local, 3);
    y = -r * 4.5; // up to -13.5%
    scale = Math.max(0.9, 1 - r * 0.028);
    rotate = -r * 0.7; // up to ~ -2.1deg
    opacity = Math.max(0.5, 1 - r * 0.17);
  }
  return { y, scale, rotate, opacity };
}

export default function Press() {
  const reducedMotion = useReducedMotion();
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 881px)").matches
      : true
  );

  useEffect(() => {
    document.title = "Press & Publications — Ōraṇi";
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 881px)");
    const cb = () => setIsDesktop(mq.matches);
    cb();
    mq.addEventListener?.("change", cb);
    return () => mq.removeEventListener?.("change", cb);
  }, []);

  // Scroll progress — runs on both desktop and mobile; only disabled
  // when the user prefers reduced motion (in which case we render a
  // plain vertical list).
  useEffect(() => {
    if (reducedMotion) return undefined;
    const el = sectionRef.current;
    if (!el) return undefined;
    let raf = null;
    const update = () => {
      raf = null;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = Math.max(1, rect.height - vh);
      const scrolled = Math.max(0, -rect.top);
      const p = Math.min(1, Math.max(0, scrolled / total));
      setProgress(p);
    };
    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reducedMotion, isDesktop]);

  // Reveal intro on enter.
  useEffect(() => {
    const root = document.querySelector(".press-page");
    if (!root) return undefined;
    const targets = root.querySelectorAll("[data-press-reveal]");
    if (reducedMotion || !("IntersectionObserver" in window)) {
      targets.forEach((n) => n.classList.add("is-in"));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    targets.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [reducedMotion, isDesktop]);

  const N = ARTICLES.length;
  const pos = progress * (N + 0.4);
  const activeIdx = Math.min(N - 1, Math.max(0, Math.floor(pos)));
  const progressPct = Math.min(1, pos / N) * 100;

  const eyebrow = (
    <span className="press-page__eyebrow" data-testid="press-eyebrow">
      Press &amp; Publications
    </span>
  );
  const heading = (
    <h1 className="press-page__title" data-testid="press-title">
      Featured across leading design publications
    </h1>
  );
  const lede = (
    <p className="press-page__lede" data-testid="press-lede">
      A selection of published stories, homes, and conversations from
      Indian and international design media.
    </p>
  );

  const progressBlock = (
    <div className="press-page__progress" aria-hidden="true">
      <span className="press-page__progress-count">
        {String(activeIdx + 1).padStart(2, "0")}
        <em> — {String(N).padStart(2, "0")}</em>
      </span>
      <span className="press-page__progress-rule">
        <span style={{ width: `${progressPct.toFixed(2)}%` }} />
      </span>
    </div>
  );

  // Reduced-motion fallback: clean static vertical list.
  if (reducedMotion) {
    return (
      <main
        className="press-page press-page--static"
        data-testid="press-page"
        aria-label="Press archive"
      >
        <section className="press-page__static-inner">
          <div className="press-page__intro is-in">
            {eyebrow}
            {heading}
            {lede}
          </div>
          <ul className="press-page__list">
            {ARTICLES.map((a) => (
              <li key={a.key} className="press-page__list-item is-in">
                <a
                  href={a.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="press-card press-card--static"
                  aria-label={`${a.name} — ${a.title}. Read feature (opens in a new tab).`}
                  data-testid={`press-card-${a.key}`}
                >
                  <div className="press-card__media">
                    <img
                      src={a.image}
                      alt={`${a.name} — ${a.title}`}
                      loading="lazy"
                    />
                  </div>
                  <div className="press-card__caption">
                    <span className="press-card__pub">{a.name}</span>
                    <span className="press-card__cta">Read feature →</span>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </section>
      </main>
    );
  }

  return (
    <main
      className={[
        "press-page",
        isDesktop ? "press-page--desktop" : "press-page--mobile",
      ].join(" ")}
      data-testid="press-page"
      aria-label="Press archive"
    >
      {/* Mobile-only intro that scrolls away naturally before the stack. */}
      {!isDesktop && (
        <section
          className="press-page__mobile-intro"
          data-press-reveal
          data-testid="press-mobile-intro"
        >
          {eyebrow}
          {heading}
          {lede}
          <span
            className="press-page__scroll-cue"
            data-testid="press-mobile-scroll-cue"
          >
            Scroll to view all features
            <span className="press-page__scroll-cue-arrow" aria-hidden="true">
              ↓
            </span>
          </span>
        </section>
      )}

      <section
        ref={sectionRef}
        className="press-page__scroll"
        style={{ "--press-count": N }}
        data-testid="press-stack-section"
      >
        <div className="press-page__sticky">
          {isDesktop && (
            <aside className="press-page__panel">
              <div className="press-page__intro" data-press-reveal>
                {eyebrow}
                {heading}
                {lede}
              </div>
              {progressBlock}
              <span
                className="press-page__scroll-cue press-page__scroll-cue--desktop"
                data-testid="press-desktop-scroll-cue"
              >
                Scroll to view all features
                <span
                  className="press-page__scroll-cue-arrow"
                  aria-hidden="true"
                >
                  ↓
                </span>
              </span>
            </aside>
          )}

          <div className="press-page__stage" data-testid="press-stage">
            {!isDesktop && (
              <div
                className="press-page__mini-progress"
                aria-hidden="true"
                data-testid="press-mobile-progress"
              >
                <span>
                  {String(activeIdx + 1).padStart(2, "0")}
                  <em> / {String(N).padStart(2, "0")}</em>
                </span>
                <span className="press-page__mini-progress-rule">
                  <span style={{ width: `${progressPct.toFixed(2)}%` }} />
                </span>
              </div>
            )}

            {ARTICLES.map((a, i) => {
              const local = pos - i;
              const { y, scale, rotate, opacity } = cardStyle(local);
              const isActive = i === activeIdx;
              return (
                <a
                  key={a.key}
                  href={a.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={[
                    "press-card",
                    isActive ? "is-active" : "",
                  ].join(" ")}
                  aria-label={`${a.name} — ${a.title}. Read feature (opens in a new tab).`}
                  data-testid={`press-card-${a.key}`}
                  style={{
                    transform: `translate(-50%, -50%) translateY(${y}%) scale(${scale}) rotate(${rotate}deg)`,
                    opacity,
                    /* Higher z when closer to active so it overlaps neighbours. */
                    zIndex: 100 - Math.round(Math.abs(local) * 10),
                    pointerEvents: opacity < 0.5 ? "none" : "auto",
                  }}
                  tabIndex={isActive ? 0 : -1}
                >
                  <div className="press-card__media">
                    <img
                      src={a.image}
                      alt={`${a.name} — ${a.title}`}
                      loading="lazy"
                    />
                  </div>
                  <div className="press-card__caption">
                    <span className="press-card__pub">{a.name}</span>
                    <span className="press-card__cta">Read feature →</span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}
